/*
 * Copyright (c) 2017 Datarista, Inc.
 */

/* eslint-disable consistent-return */

import { polyfill } from 'es6-promise';
import { push } from 'react-router-redux';
import { startAction, completeAction, handleError, reportError, reportSuccess } from './common';
import { storeWithExpiration } from '../utils/common';
import { USERS_INVITE_NOTICE_EXPIRATION_TIME } from '../config/app_config';
import { accountServiceClient } from '../utils/api';

polyfill();

export const actionTypes = {
  USER_TYPES_FETCH_ALL_SUCCESS: 'USER_TYPES_FETCH_ALL_SUCCESS',
  USER_TYPES_FETCH_ALL_ERROR: 'USER_TYPES_FETCH_ALL_ERROR',
  USER_FETCH_ALL_SUCCESS: 'USER_FETCH_ALL_SUCCESS',
  USER_FETCH_ALL_ERROR: 'USER_FETCH_ALL_ERROR',
  USER_INVITE_FORM_SUBMIT: 'USER_INVITE_FORM_SUBMIT',
  USER_INVITE_SUCCESS_CLOSE: 'USER_INVITE_SUCCESS_CLOSE',
  USER_EDIT_SUCCESS: 'USER_EDIT_SUCCESS',
  USER_EDIT_SUCCESS_BY_ADMIN: 'USER_EDIT_SUCCESS_BY_ADMIN',
  UPLOAD_COMPANY_LOGO_SUCCESS: 'UPLOAD_COMPANY_LOGO_SUCCESS',
};

// Connect to the API
const swaggerApi = accountServiceClient();

// Create user list action
const receiveUsers = users => ({
  type: actionTypes.USER_FETCH_ALL_SUCCESS,
  users,
});

// List all users for the account
export function listUsers(accountId, token) {
  const actionText = 'Listing all users';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.accounts
        .listUsers({
          Authorization: `bearer ${token}`,
          account_id: accountId,
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const usersInfo = response.obj;
            dispatch(receiveUsers(usersInfo));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          if (err.status === 401) dispatch(push('/notfound'));
          handleError(err, dispatch, actionText);
        });
    });
  };
}

// Creates user type list action
const receiveUserTypes = userTypes => ({
  type: actionTypes.USER_TYPES_FETCH_ALL_SUCCESS,
  userTypes,
});

// receives the list of user types from the server
export function listUserTypes(accountId, token) {
  const actionText = 'Listing all user types';

  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.accounts
        .listUserTypes({
          Authorization: `bearer ${token}`,
          account_id: accountId,
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const userTypeList = response.obj;
            dispatch(receiveUserTypes(userTypeList));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

// create a user invite action
const inviteUser = inviteData => ({
  type: actionTypes.USER_INVITE_FORM_SUBMIT,
  firstName: inviteData.firstName,
  lastName: inviteData.lastName,
  notification: !storeWithExpiration.get('NoSignupNotification') || null,
});

// a function that creates a new user on the test server
// if successful, it sends an email to the new user
export function createInviteUser(token, id, inviteData) {
  const actionText = 'Creating Account';

  // make the user type correct for the account type
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.accounts
        .createUser({
          Authorization: `bearer ${token}`,
          account_id: id,
          body: {
            email: inviteData.email,
            userTypeId: parseInt(inviteData.accessLevel, 10),
            firstName: inviteData.firstName,
            lastName: inviteData.lastName,
            phone: inviteData.phoneNumber,
            accountId: id,
            status: 1,
            isPrimary: inviteData.isPrimary,
          },
        })
        .then(response => {
          // If the server responds correctly, continue
          // and get the id of the new user
          if (response.status >= 200 && response.status < 300) {
            const newinfo = response.obj;
            const newid = newinfo.id;
            dispatch(listUsers(id, token));
            return newid;
          }
          return Promise.reject(response);
        })
        .then(newid => {
          client.auth
            .sendInvite({
              Authorization: `bearer ${token}`,
              user_id: newid,
            })
            .then(response => {
              // If the server responds, continue, and finish action
              if (response.status >= 200 && response.status <= 500) {
                dispatch(inviteUser(inviteData));
                completeAction(dispatch, actionText);
              } else {
                return Promise.reject(response);
              }
            });
        })
        .catch(err => {
          const statusText = JSON.parse(err.statusText);
          let { message } = statusText;
          if (err.status === 409) {
            message = 'Error Inviting New User: email was already in use';
          } else if (err.status === 500) {
            message = 'Error Inviting New User: please try again';
          }
          const error = new Error(message);
          reportError(dispatch, error);
        });
    });
  };
}

export function resendUserInvite(token, id, inviteData) {
  const actionText = 'Resending User Invite Email';

  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.auth
        .sendInvite({
          Authorization: `bearer ${token}`,
          user_id: id,
        })
        .then(response => {
          // If the server responds, continue, and finish action
          if (response.status >= 200 && response.status <= 500) {
            dispatch(inviteUser(inviteData));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          const statusText = JSON.parse(err.statusText);
          let { message } = statusText;
          if (err.status === 409) {
            message = 'Error Inviting New User: email was already in use';
          } else if (err.status === 500) {
            message = 'Error Inviting New User: please try again';
          }
          const error = new Error(message);
          reportError(dispatch, error);
        });
    });
  };
}

export function closeUserSuccess(notificationInfo) {
  const action = {
    type: actionTypes.USER_INVITE_SUCCESS_CLOSE,
    notification: notificationInfo,
  };
  return dispatch => {
    if (notificationInfo) {
      storeWithExpiration.set('NoSignupNotification', true, USERS_INVITE_NOTICE_EXPIRATION_TIME);
    }
    dispatch(action);
  };
}

const editUser = (userData, isUser) => ({
  type: actionTypes.USER_EDIT_SUCCESS,
  user: userData,
  isUser,
});

const editUserByAdmin = (userData, isUser) => ({
  type: actionTypes.USER_EDIT_SUCCESS_BY_ADMIN,
  user: userData,
  isUser,
});

export function updateUserAccount(userData, isUser, token) {
  const actionText = 'Update user account';

  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.accounts
        .updateUser({
          Authorization: `bearer ${token}`,
          account_id: userData.accountId,
          user_id: userData.id,
          body: userData,
        })
        .then(response => {
          let successMessage = '';
          // If the server responds, continue, and finish action
          if (response.status >= 200 && response.status <= 500) {
            if (isUser) {
              dispatch(editUser(userData, isUser));
              successMessage = 'You have successfully edited your account';
            } else {
              dispatch(editUserByAdmin(userData, isUser));
              successMessage = `You have successfully edited ${userData.firstName}'s account`;
            }
            completeAction(dispatch, actionText);
            reportSuccess(dispatch, successMessage, null);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          const statusText = JSON.parse(err.statusText);
          let { message } = statusText;
          if (err.status === 500) {
            message = 'Error Updating User: please try again';
          }
          const error = new Error(message);
          reportError(dispatch, error);
        });
    });
  };
}

const uploadCompanyLogoSuccess = account => ({
  type: actionTypes.UPLOAD_COMPANY_LOGO_SUCCESS,
  account,
});

export function uploadCompanyLogo(accountId, imgFile, token) {
  const actionText = 'Uploading Company Logo';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.accounts
        .uploadAccountLogo({
          Authorization: `bearer ${token}`,
          account_id: accountId,
          image: imgFile,
        })
        .then(response => {
          if (response.status >= 200 && response.status <= 300) {
            const account = JSON.parse(response.data);
            return Promise.resolve(account);
          }
          return Promise.reject(response);
        })
        .then(account => {
          dispatch(uploadCompanyLogoSuccess(account));
          completeAction(dispatch, actionText);
          reportSuccess(dispatch, 'Your upload was successful', null);
        })
        .catch(err => {
          const statusText = JSON.parse(err.statusText);
          const { message } = statusText;
          const error = new Error(message);
          reportError(dispatch, error);
        });
    });
  };
}

export function removeCompanyLogo(accountId, token) {
  const actionText = 'Uploading Company Logo';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.accounts
        .removeAccountLogo({
          Authorization: `bearer ${token}`,
          account_id: accountId,
        })
        .then(response => {
          if (response.status >= 200 && response.status <= 300) {
            const account = JSON.parse(response.data);
            return Promise.resolve(account);
          }
          return Promise.reject(response);
        })
        .then(account => {
          dispatch(uploadCompanyLogoSuccess(account));
          completeAction(dispatch, actionText);
          reportSuccess(dispatch, 'Your logo was removed successfully', null);
        })
        .catch(err => {
          const statusText = JSON.parse(err.statusText);
          const { message } = statusText;
          const error = new Error(message);
          reportError(dispatch, error);
        });
    });
  };
}
